import { LanguageType } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';

import { backofficeService } from '../../../../application/BackofficeService';
import { TranslationDomain } from '../../../../domain/translation/TranslationDomain';
import useErrorMiddleware from '../../../../hooks/errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/snackbarMessage/useShowSnackbarMessage';

interface UseCreateTranslationProps {
  language: LanguageType;
  key: string;
  value: string;
  domain: TranslationDomain;
}

type UseCreateTranslationResponse = ServiceResponse<
  ({ language, key, value }: UseCreateTranslationProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useCreateTranslation = (): UseCreateTranslationResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    ({ language, key, value, domain }: UseCreateTranslationProps): Promise<void> => {
      return backofficeService.createTranslation({
        domain,
        language,
        key,
        value,
      });
    },
    {
      onError: (err: any) => {
        console.info(`Planner Backoffice Create Translation Error:`, err);
        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    run: ({ language, key, value, domain }: UseCreateTranslationProps) => mutate({ language, key, value, domain }),
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
  };
};
